import {
  GetAmazonConnectIntegrationSettingQuery,
  GetInboundApiKeysQuery,
  InboundApiKeyStatus,
  GetAutoAssessmentSettingQuery,
  GetBiztelIntegrationSettingQuery,
  GetKaikuraIntegrationSettingQuery,
  GetOutboundIntegrationSettingQuery,
  ValidateSalesforceIntegrationSettingQuery,
} from '../../../_generated/graphql'

export const mappingAutoAssessmentSetting = (
  fetchedData: GetAutoAssessmentSettingQuery['getAutoAssessmentSetting'],
): AutoAssessmentSetting | null => {
  if (!fetchedData) {
    return null
  }

  return {
    id: fetchedData.id,
    targetUsers: fetchedData.targetUsers.map((user) => ({
      id: user.id,
      name: user.name,
      email: user.email,
      departmentName: user.departmentName || null,
      thumbnailPath: user.thumbnailPath || null,
      reportTo: user.reportTo
        ? {
            id: user.reportTo.id,
            name: user.reportTo.name,
          }
        : null,
      license: user.license
        ? {
            id: user.license.id,
            plan: user.license.plan,
            userMonthlyLimitSeconds: user.license.userMonthlyLimitSeconds,
            extraChargeLimitSeconds: user.license.extraChargeLimitSeconds,
            usedSeconds: user.license.usedSeconds,
          }
        : null,
    })),
    targetSkillMap: {
      id: fetchedData.targetSkillMap.id,
      name: fetchedData.targetSkillMap.name,
    },
    targetPhaseIds: fetchedData.targetPhaseIds,
    minTransferDurationSeconds:
      fetchedData.minTransferDurationSeconds != null
        ? fetchedData.minTransferDurationSeconds
        : null,
    syncStartDate: new Date(fetchedData.syncStartDate),
    active: fetchedData.active,
    whiteList: fetchedData.whiteList,
    blackList: fetchedData.blackList,
  }
}

export const mappingOutboundIntegrationSetting = (
  fetchedData: GetOutboundIntegrationSettingQuery['getOutboundIntegrationSetting'],
): OutboundIntegrationSetting | null => {
  if (!fetchedData) {
    return null
  }

  return {
    id: fetchedData.id,
    targetUsers: fetchedData.targetUsers.map((user) => ({
      id: user.id,
      name: user.name,
      email: user.email,
      departmentName: user.departmentName || null,
      thumbnailPath: user.thumbnailPath || null,
      reportTo: user.reportTo
        ? {
            id: user.reportTo.id,
            name: user.reportTo.name,
          }
        : null,
      license: user.license
        ? {
            id: user.license.id,
            plan: user.license.plan,
            userMonthlyLimitSeconds: user.license.userMonthlyLimitSeconds,
            extraChargeLimitSeconds: user.license.extraChargeLimitSeconds,
            usedSeconds: user.license.usedSeconds,
          }
        : null,
    })),
  }
}

export const mappingKaikuraIntegrationSetting = (
  fetchedData: GetKaikuraIntegrationSettingQuery['getAdminOrg']['kaikuraIntegrationSetting'],
): KaikuraIntegrationSetting | null => {
  if (!fetchedData) {
    return null
  }

  return {
    active: fetchedData.active,
    tagAllowlist: fetchedData.tagAllowlist,
    skillMapId: fetchedData.skillMapId ?? null,
    lastSyncedUpdateDatetime: fetchedData.lastSyncedUpdateDatetime
      ? new Date(fetchedData.lastSyncedUpdateDatetime)
      : null,
  }
}

export const mappingBiztelIntegrationSetting = (
  fetchedData: GetBiztelIntegrationSettingQuery['getAdminOrg']['biztelIntegrationSetting'],
): BiztelIntegrationSetting | null => {
  if (!fetchedData) {
    return null
  }

  return {
    active: fetchedData.active,
    fqdn: fetchedData.fqdn,
    skillMapId: fetchedData.skillMapId ?? null,
    queueIds: fetchedData.queueIds,
    outboundCallerNumbers: fetchedData.outboundCallerNumbers,
    excludedExternalNumbers: fetchedData.excludedExternalNumbers,
    lastSyncedUpdateDatetime: fetchedData.lastSyncedUpdateDatetime
      ? new Date(fetchedData.lastSyncedUpdateDatetime)
      : null,
  }
}

export const mappingSalesforceIntegrationSetting = (
  fetchedData: ValidateSalesforceIntegrationSettingQuery['validateSalesforceIntegrationSetting'],
): SalesforceValidationResult => {
  return {
    isValid: fetchedData.isValid,
    errors: fetchedData.errors.map((error) => ({
      code: error.code,
      message: error.message,
      objectName: error.objectName || undefined,
      fieldName: error.fieldName || undefined,
      expectedType: error.expectedType || undefined,
      actualType: error.actualType || undefined,
      minLength: error.minLength || undefined,
    })),
  }
}

export interface TargetUser {
  id: string
  name: string
  email: string
  departmentName: string | null
  thumbnailPath: string | null
  reportTo: {
    id: string
    name: string
  } | null
  license: {
    id: string
    plan: string
    userMonthlyLimitSeconds: number
    extraChargeLimitSeconds: number
    usedSeconds: number
  } | null
}

export interface TargetSkillMap {
  id: string
  name: string
}

export interface AutoAssessmentSetting {
  id: string
  targetUsers: TargetUser[]
  targetSkillMap: TargetSkillMap
  targetPhaseIds: string[]
  minTransferDurationSeconds: number | null
  syncStartDate: Date
  active: boolean
  whiteList: string[]
  blackList: string[]
}

export interface OutboundIntegrationSetting {
  id: string
  targetUsers: TargetUser[]
}

export interface KaikuraIntegrationSetting {
  active: boolean
  tagAllowlist: string[]
  skillMapId: string | null
  lastSyncedUpdateDatetime: Date | null
}

export interface BiztelIntegrationSetting {
  active: boolean
  fqdn: string
  skillMapId: string | null
  /** 着信の取り込み対象 queue_id リスト。空 = 全キュー取り込み */
  queueIds: string[]
  /** 発信の取り込み対象の自社側通知番号 (caller_id) リスト。空 = 全発信取り込み */
  outboundCallerNumbers: string[]
  /** 取り込みから除外する通話相手 (顧客側) の電話番号リスト。空 = 除外なし */
  excludedExternalNumbers: string[]
  lastSyncedUpdateDatetime: Date | null
}

export interface AmptalkKeys {
  apiKey: string
  authKey: string
}

export interface SalesforceValidationError {
  code: string
  message: string
  objectName?: string
  fieldName?: string
  expectedType?: string
  actualType?: string
  minLength?: number
}

export interface SalesforceValidationResult {
  isValid: boolean
  errors: SalesforceValidationError[]
}

export interface AmazonConnectIntegrationSetting {
  active: boolean
  skillMapId: string | null
  allowedCidrs: string[]
  lastReceivedAt: Date | null
}

export interface InboundApiKey {
  keyId: string
  last4: string
  label: string | null
  createdAt: Date
  createdBy: string
  createdByName: string | null
  lastUsedAt: Date | null
  expiresAt: Date
  revokedAt: Date | null
  revokedBy: string | null
  revokeReason: string | null
  status: InboundApiKeyStatus
}

/** GraphQL の nullable 設定を画面の日時型へ変換する。 */
export function mappingAmazonConnectIntegrationSetting(
  data: GetAmazonConnectIntegrationSettingQuery['getAdminOrg']['amazonConnectIntegrationSetting'],
): AmazonConnectIntegrationSetting | null {
  if (!data) return null
  return {
    active: data.active,
    skillMapId: data.skillMapId ?? null,
    allowedCidrs: data.allowedCidrs,
    lastReceivedAt:
      data.lastReceivedAt == null ? null : new Date(data.lastReceivedAt),
  }
}

/** キーのメタデータと日時を画面用に変換する。 */
export function mappingInboundApiKeys(
  data: GetInboundApiKeysQuery['getAdminOrg']['inboundApiKeys'],
): InboundApiKey[] {
  return data.map((key) => {
    return {
      keyId: key.keyId,
      last4: key.last4,
      label: key.label ?? null,
      createdAt: new Date(key.createdAt),
      createdBy: key.createdBy,
      createdByName: key.createdByName ?? null,
      lastUsedAt: key.lastUsedAt == null ? null : new Date(key.lastUsedAt),
      expiresAt: new Date(key.expiresAt),
      revokedAt: key.revokedAt == null ? null : new Date(key.revokedAt),
      revokedBy: key.revokedBy ?? null,
      revokeReason: key.revokeReason ?? null,
      status: key.status,
    }
  })
}
